import { colors } from '../../styles/theme';
import type { ClientMessage } from '../../types/contracts';

interface Props {
  message: ClientMessage;
  /** Open the assistance modal pre-set to "await contact". */
  onAwaitContact: () => void;
  /** Open the assistance modal pre-set to "schedule call". */
  onScheduleCall: () => void;
}

/**
 * Curated banner shown to the member when staff returns the contract to
 * them. Displays only the structured `{summary, issues[]}` from
 * state_transitions_log.client_visible_notes. Internal staff comments are
 * NEVER passed into this component (the backend redacts them for client
 * viewers — see workflow.get_case_timeline).
 */
export default function ClientMessageBanner({
  message,
  onAwaitContact,
  onScheduleCall,
}: Readonly<Props>): JSX.Element {
  return (
    <div
      className="rounded-2xl p-6 mb-6"
      style={{
        background: colors.warningBg,
        border: `1px solid ${colors.dangerBorder}`,
      }}
      role="alert"
    >
      <h2
        className="text-base font-semibold mb-2 flex items-center gap-2"
        style={{ color: colors.warning }}
      >
        <span aria-hidden="true">⚠</span>
        {/* */}Your contract needs corrections
      </h2>
      <p className="text-sm mb-3" style={{ color: colors.inkSoft }}>
        {message.summary}
      </p>
      {message.issues.length > 0 && (
        <ul className="text-sm list-disc pl-5 space-y-1 mb-4" style={{ color: colors.inkSoft }}>
          {message.issues.map((issue, idx) => (
            <li key={`${idx}-${issue.slice(0, 24)}`}>{issue}</li>
          ))}
        </ul>
      )}
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={onAwaitContact}
          className="inline-flex items-center justify-center px-4 py-2 rounded-lg text-sm font-semibold transition-colors"
          style={{
            background: colors.surface,
            color: colors.ink,
            border: `1px solid ${colors.border}`,
          }}
        >
          Await contact
        </button>
        <button
          type="button"
          onClick={onScheduleCall}
          className="inline-flex items-center justify-center px-4 py-2 rounded-lg text-sm font-semibold transition-colors"
          style={{
            background: colors.surface,
            color: colors.ink,
            border: `1px solid ${colors.border}`,
          }}
        >
          Schedule a call
        </button>
      </div>
    </div>
  );
}
